import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { resolveDimension } from '../../three/core/dimension/DimensionEngine';
import {
  dragOffsetM,
  offsetPixelsPerMeter,
} from '../../three/core/dimension/ProjectionEngine';
import {
  openDimensionEditor,
  openDimensionMenu,
  removeDimension,
  selectDimension,
  setDimensionOrphans,
  updateDimension,
} from '../../store/reducers/dimension.reducer';
import { buildPartIndex } from '../../three/core/dimension/PartResolver';
import { DimensionSvgRenderer } from './DimensionSvgRenderer';
import { ManualDimensionController } from './ManualDimensionController';

/** Map instanceId -> group của các module đang có trong cabinet hiện tại. */
export const buildGroupIndex = sceneManager => {
  const index = new Map();
  const modules =
    sceneManager?.cabinetManager?.getCurrentCabinet?.()?.getAllModules?.() ??
    [];
  modules.forEach(m => {
    const id = m.group?.userData?.instanceId;
    if (id) index.set(id, m.group);
  });
  return index;
};

/**
 * Chỉ mục mặt đá / ốp tường hiện có (xem PartResolver.buildPartIndex). Chúng bị
 * tạo lại mỗi khi module đổi nên phải tra lại mỗi frame.
 */
export const buildScenePartIndex = sceneManager =>
  buildPartIndex(sceneManager?.createGroupPlane?.groupPlanes?.values?.() ?? []);

const hitOf = event => {
  const part = event.target.closest?.('[data-part]');
  const group = event.target.closest?.('[data-dim-id]');
  if (!part || !group) return null;
  return {
    id: group.getAttribute('data-dim-id'),
    part: part.getAttribute('data-part'),
  };
};

/**
 * Lớp SVG vẽ DIM phủ lên canvas 3D. Đặt bên trong #container__canvas.
 * Toàn bộ vẽ lại mỗi frame qua hook sau-render của SceneManager; React chỉ
 * render lại khi danh sách DIM / đơn vị thay đổi.
 *
 * Tương tác: kéo đường DIM để đổi khoảng cách, kéo chữ để dời nhãn,
 * double-click mở bảng thuộc tính, chuột phải mở menu.
 */
const DimensionOverlay = ({ sceneManager }) => {
  const svgRef = useRef(null);
  const store = useStore();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { enabled, items, unit, selectedId, mode, layers } = useSelector(
    state => state.dimension
  );
  // Controller của chế độ đo thủ công (chỉ tồn tại khi mode === 'manual')
  const manualRef = useRef(null);
  // Ref để callback mỗi frame luôn đọc dữ liệu mới nhất mà không phải đăng ký lại
  const dataRef = useRef({ items, unit, selectedId, layers });
  dataRef.current = { items, unit, selectedId, layers };

  // Nhãn của marker snap. Cập nhật qua ref (không đưa `t` vào deps của effect
  // bên dưới) để đổi ngôn ngữ hay render lại không làm mất điểm đang chọn dở.
  const snapLabels = {
    corner: t('Corner'),
    edge: t('Edge midpoint'),
    face: t('Face center'),
    center: t('Center'),
    onEdge: t('On edge'),
    surface: t('Point on surface'),
    noteText: t('Note'),
  };
  if (manualRef.current) manualRef.current.labels = snapLabels;
  const snapLabelsRef = useRef(snapLabels);
  snapLabelsRef.current = snapLabels;

  // Phím Delete/Backspace: designer dùng nó để xoá MODULE đang chọn. Khi người dùng
  // vừa bấm vào một DIM thì phím phải xoá DIM đó (không được xoá module). Bấm lên
  // canvas (chọn module, bỏ chọn...) thì DIM hết được chọn và phím trả về cho designer.
  useEffect(() => {
    if (!enabled) return;
    const dom = sceneManager?.renderer?.domElement;
    const onKey = e => {
      if (e.code !== 'Delete' && e.code !== 'Backspace') return;
      const {
        selectedId,
        mode: currentMode,
        items: list,
      } = store.getState().dimension;
      if (!selectedId || currentMode !== 'idle') return;
      if (!list.some(i => i.id === selectedId)) return;
      const active = document.activeElement;
      if (
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(active?.tagName) ||
        active?.isContentEditable
      ) {
        return; // đang gõ chữ: để ô nhập xử lý
      }
      e.preventDefault();
      // Pha capture + chặn ngay: handler của designer (pha nổi bọt) không chạy
      e.stopImmediatePropagation();
      dispatch(removeDimension(selectedId));
    };
    const onCanvasDown = () => {
      if (
        store.getState().dimension.selectedId &&
        store.getState().dimension.mode === 'idle'
      ) {
        dispatch(selectDimension(null));
      }
    };
    document.addEventListener('keydown', onKey, true);
    dom?.addEventListener?.('pointerdown', onCanvasDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      dom?.removeEventListener?.('pointerdown', onCanvasDown, true);
    };
  }, [sceneManager, enabled, store, dispatch]);

  // Chế độ đo thủ công: gắn/gỡ listener trên canvas theo `mode`
  useEffect(() => {
    if (
      !enabled ||
      !['manual', 'annotate'].includes(mode) ||
      !sceneManager?.renderer
    ) {
      return;
    }
    const controller = new ManualDimensionController({
      sceneManager,
      store,
      getGroupIndex: () => buildGroupIndex(sceneManager),
      getPartIndex: () => buildScenePartIndex(sceneManager),
      labels: snapLabelsRef.current,
    });
    controller.attach();
    manualRef.current = controller;
    return () => {
      controller.detach();
      manualRef.current = null;
    };
  }, [sceneManager, enabled, mode, store]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!enabled || !sceneManager?.addAfterRenderListener || !svg) return;

    const renderer = new DimensionSvgRenderer(svg);
    let lastOrphanKey = '';
    const removeListener = sceneManager.addAfterRenderListener(camera => {
      const dom = sceneManager.renderer?.domElement;
      if (!dom || !camera) return;
      const index = buildGroupIndex(sceneManager);
      const partIndex = buildScenePartIndex(sceneManager);
      const width = dom.clientWidth;
      const height = dom.clientHeight;
      // Đang đo thủ công: thêm marker snap + DIM xem trước, tắt tương tác kéo
      const manual = manualRef.current?.frame(
        camera,
        width,
        height,
        index,
        partIndex
      );
      const orphanIds = renderer.render(
        manual?.previewItem
          ? [...dataRef.current.items, manual.previewItem]
          : dataRef.current.items,
        {
          camera,
          width,
          height,
          unit: dataRef.current.unit,
          selectedId: dataRef.current.selectedId,
          getGroup: id => index.get(id) ?? null,
          getPartMeshes: partIndex.getPartMeshes,
          layers: dataRef.current.layers,
          interactive: !manual,
          markers: manual?.markers ?? [],
        }
      );
      // Chỉ dispatch khi tập DIM mất anchor thay đổi
      const key = orphanIds.join(',');
      if (key !== lastOrphanKey) {
        lastOrphanKey = key;
        dispatch(setDimensionOrphans(orphanIds));
      }
    });

    // ---- Tương tác ----
    let drag = null;

    const onMove = e => {
      if (!drag) return;
      const delta = { x: e.clientX - drag.startX, y: e.clientY - drag.startY };
      if (drag.part === 'text') {
        dispatch(
          updateDimension({
            id: drag.id,
            changes: {
              labelShift: [
                drag.startShift[0] + delta.x,
                drag.startShift[1] + delta.y,
              ],
            },
          })
        );
      } else {
        dispatch(
          updateDimension({
            id: drag.id,
            changes: {
              offsetM: dragOffsetM(drag.startOffsetM, delta, drag.pxPerMeter),
            },
          })
        );
      }
    };

    const endDrag = () => {
      if (!drag) return;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      const controls = sceneManager.controls;
      if (controls && drag.controlsWereEnabled) controls.enabled = true;
      drag = null;
    };

    const onPointerDown = e => {
      if (e.button !== 0) return;
      const hit = hitOf(e);
      if (!hit) return;
      e.preventDefault();
      e.stopPropagation();
      dispatch(selectDimension(hit.id));

      const dim = store.getState().dimension.items.find(i => i.id === hit.id);
      const dom = sceneManager.renderer?.domElement;
      if (!dim || !dom) return;

      let pxPerMeter = null;
      if (hit.part === 'line') {
        const index = buildGroupIndex(sceneManager);
        const resolved = resolveDimension(dim, id => index.get(id) ?? null, {
          getPartMeshes: buildScenePartIndex(sceneManager).getPartMeshes,
        });
        if (resolved) {
          pxPerMeter = offsetPixelsPerMeter(
            resolved,
            sceneManager.camera,
            dom.clientWidth,
            dom.clientHeight
          );
        }
      }
      const controls = sceneManager.controls;
      drag = {
        id: hit.id,
        part: hit.part,
        startX: e.clientX,
        startY: e.clientY,
        startOffsetM: dim.offsetM ?? 0,
        startShift: dim.labelShift ?? [0, 0],
        pxPerMeter,
        controlsWereEnabled: !!controls?.enabled,
      };
      // Không để OrbitControls xoay camera trong lúc kéo DIM
      if (controls) controls.enabled = false;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    };

    const onDoubleClick = e => {
      const hit = hitOf(e);
      if (!hit) return;
      e.preventDefault();
      e.stopPropagation();
      dispatch(openDimensionEditor(hit.id));
    };

    const onContextMenu = e => {
      const hit = hitOf(e);
      if (!hit) return;
      e.preventDefault();
      e.stopPropagation();
      dispatch(openDimensionMenu({ id: hit.id, x: e.clientX, y: e.clientY }));
    };

    svg.addEventListener('pointerdown', onPointerDown);
    svg.addEventListener('dblclick', onDoubleClick);
    svg.addEventListener('contextmenu', onContextMenu);

    return () => {
      endDrag();
      svg.removeEventListener('pointerdown', onPointerDown);
      svg.removeEventListener('dblclick', onDoubleClick);
      svg.removeEventListener('contextmenu', onContextMenu);
      removeListener();
      renderer.dispose();
    };
  }, [sceneManager, enabled, dispatch, store]);

  if (!enabled) return null;

  return (
    <svg
      ref={svgRef}
      id="dimension-overlay"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        // Gốc không bắt chuột; chỉ đường DIM và chữ (pointer-events riêng) bắt
        pointerEvents: 'none',
        zIndex: 5,
        overflow: 'hidden',
      }}
    />
  );
};

export default DimensionOverlay;
